/**
 * Users.jsx — Redesigned with Dark Luxury Fintech aesthetic
 * Cohérent avec AdminHome + Orders : navy profond, accents ambre/Pi, glassmorphism
 */

import { useEffect, useState } from "react";
import { collection, getDocs, doc, updateDoc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/config";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Phone, Search, Download, Eye,
  UserX, CheckCircle, Calendar, LogIn, Chrome,
  ExternalLink, Package, ShoppingBag, Slash, X,
  ArrowUp, ArrowDown, RefreshCw
} from "react-feather";
import { Users as UsersIcon } from "react-feather";
import { toast } from "react-toastify";

// ─── Design atoms ─────────────────────────────────────────────────────────

const GlassPanel = ({ children, style = {} }) => (
  <div style={{ background:"rgba(17,24,39,.85)", border:"1px solid rgba(255,255,255,.06)", borderRadius:16, backdropFilter:"blur(16px)", ...style }}>
    {children}
  </div>
);

const MiniStat = ({ label, value, accent = "#f59e0b", icon: Icon, index = 0 }) => (
  <motion.div
    initial={{ opacity:0, y:18 }} animate={{ opacity:1, y:0 }} transition={{ delay: index * .07, duration:.45, ease:[.22,1,.36,1] }}
    whileHover={{ y:-3, transition:{ duration:.15 } }}
    style={{ background:"rgba(17,24,39,.85)", border:`1px solid ${accent}33`, borderRadius:14, padding:"14px 16px", boxShadow:`0 0 24px ${accent}0d`, position:"relative", overflow:"hidden" }}
  >
    <div style={{ position:"absolute", top:-24, right:-24, width:72, height:72, borderRadius:"50%", background:`${accent}18`, filter:"blur(20px)", pointerEvents:"none" }} />
    <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", position:"relative" }}>
      <div>
        <p style={{ fontSize:9, textTransform:"uppercase", letterSpacing:".14em", color:"#475569", marginBottom:8 }}>{label}</p>
        <p style={{ fontSize:22, fontWeight:700, color:"#fff", lineHeight:1 }}>{value}</p>
      </div>
      {Icon && (
        <div style={{ width:32, height:32, borderRadius:9, background:`${accent}18`, border:`1px solid ${accent}33`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          <Icon size={15} color={accent} strokeWidth={1.8} />
        </div>
      )}
    </div>
    <motion.div initial={{ scaleX:0 }} animate={{ scaleX:1 }} transition={{ duration:.5, delay:.2 + index*.07 }}
      style={{ position:"absolute", bottom:0, left:0, right:0, height:2, background:accent, opacity:.5, transformOrigin:"left" }} />
  </motion.div>
);

const Btn = ({ children, onClick, variant="ghost", size="sm", disabled=false, style={} }) => {
  const V = {
    ghost:  { background:"rgba(255,255,255,.06)", color:"#cbd5e1", border:"1px solid rgba(255,255,255,.08)" },
    amber:  { background:"rgba(245,158,11,.15)",  color:"#f59e0b", border:"1px solid rgba(245,158,11,.3)"  },
    green:  { background:"rgba(16,185,129,.15)",  color:"#34d399", border:"1px solid rgba(16,185,129,.3)"  },
    red:    { background:"rgba(239,68,68,.15)",   color:"#f87171", border:"1px solid rgba(239,68,68,.3)"   },
    violet: { background:"rgba(139,92,246,.15)",  color:"#a78bfa", border:"1px solid rgba(139,92,246,.3)"  },
  };
  const v = V[variant] || V.ghost;
  const pad = size==="xs" ? "3px 8px" : size==="sm" ? "5px 12px" : "8px 18px";
  return (
    <button onClick={onClick} disabled={disabled} style={{ ...v, padding:pad, borderRadius:8, fontSize:11, fontWeight:600, cursor:disabled?"not-allowed":"pointer", opacity:disabled?.45:1, display:"inline-flex", alignItems:"center", gap:5, transition:"opacity .15s", whiteSpace:"nowrap", ...style }}>
      {children}
    </button>
  );
};

const DarkInput = ({ value, onChange, placeholder, style={} }) => (
  <input value={value} onChange={onChange} placeholder={placeholder}
    style={{ background:"rgba(255,255,255,.04)", border:"1px solid rgba(255,255,255,.1)", borderRadius:10, padding:"8px 12px", color:"#e2e8f0", fontSize:12, width:"100%", outline:"none", ...style }} />
);

const DarkSelect = ({ value, onChange, children, style={} }) => (
  <select value={value} onChange={onChange}
    style={{ background:"#111827", border:"1px solid rgba(255,255,255,.1)", borderRadius:10, padding:"8px 12px", color:"#e2e8f0", fontSize:12, outline:"none", appearance:"none", cursor:"pointer", ...style }}>
    {children}
  </select>
);

const Avatar = ({ user, size=40 }) => {
  const gradients = [["#f59e0b","#d97706"],["#8b5cf6","#6d28d9"],["#14b8a6","#0d9488"],["#3b82f6","#2563eb"],["#f43f5e","#e11d48"],["#a78bfa","#7c3aed"]];
  const g = gradients[(user.name?.charCodeAt(0)||0) % gradients.length];
  return (
    <div style={{ width:size, height:size, borderRadius:"50%", background: user.profilePic ? "none" : `linear-gradient(135deg,${g[0]},${g[1]})`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, overflow:"hidden", border:"2px solid rgba(255,255,255,.1)" }}>
      {user.profilePic
        ? <img src={user.profilePic} alt={user.name} style={{ width:"100%", height:"100%", objectFit:"cover" }} />
        : <span style={{ fontSize:size*.38, fontWeight:700, color:"#fff" }}>{(user.name||"?").charAt(0).toUpperCase()}</span>
      }
    </div>
  );
};

const StatusBadge = ({ banned }) => banned
  ? <span style={{ background:"rgba(239,68,68,.12)", color:"#f87171", boxShadow:"inset 0 0 0 1px rgba(239,68,68,.3)", fontSize:10, fontWeight:700, letterSpacing:".06em", textTransform:"uppercase", padding:"3px 8px", borderRadius:20 }}>Banned</span>
  : <span style={{ background:"rgba(16,185,129,.12)", color:"#34d399", boxShadow:"inset 0 0 0 1px rgba(16,185,129,.3)", fontSize:10, fontWeight:700, letterSpacing:".06em", textTransform:"uppercase", padding:"3px 8px", borderRadius:20 }}>Active</span>;

const ProviderBadge = ({ provider }) => {
  const isGoogle = provider?.toLowerCase() === "google";
  return (
    <span style={{ background: isGoogle ? "rgba(66,133,244,.12)" : "rgba(245,158,11,.12)", color: isGoogle ? "#60a5fa" : "#f59e0b", fontSize:10, fontWeight:600, padding:"3px 8px", borderRadius:20, display:"inline-flex", alignItems:"center", gap:4 }}>
      {isGoogle ? <Chrome size={9}/> : <Mail size={9}/>}
      {isGoogle ? "Google" : "Email"}
    </span>
  );
};

const MetaCell = ({ label, value, span=false }) => (
  <div style={{ ...(span?{gridColumn:"1 / -1"}:{}), background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,255,255,.05)", borderRadius:10, padding:"10px 12px" }}>
    <p style={{ fontSize:9, textTransform:"uppercase", letterSpacing:".12em", color:"#334155", marginBottom:5 }}>{label}</p>
    <div style={{ fontSize:12, color:"#94a3b8", fontWeight:500 }}>{value || "—"}</div>
  </div>
);

const OrderBadge = ({ status }) => {
  const map = { Delivered:["rgba(16,185,129,.12)","#34d399","rgba(16,185,129,.3)"], Shipped:["rgba(139,92,246,.12)","#a78bfa","rgba(139,92,246,.3)"], Placed:["rgba(245,158,11,.12)","#fbbf24","rgba(245,158,11,.3)"], Approved:["rgba(56,189,248,.12)","#38bdf8","rgba(56,189,248,.3)"], Declined:["rgba(239,68,68,.12)","#f87171","rgba(239,68,68,.3)"] };
  const [bg,color,ring] = map[status]||["rgba(100,116,139,.12)","#94a3b8","rgba(100,116,139,.3)"];
  return <span style={{ background:bg, color, boxShadow:`inset 0 0 0 1px ${ring}`, fontSize:10, fontWeight:700, letterSpacing:".06em", textTransform:"uppercase", padding:"2px 7px", borderRadius:20 }}>{status}</span>;
};

// ═══════════════════════════════════════════════════════════════════════════
const Users = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("view");
  const [stats, setStats] = useState({ total:0, active:0, banned:0, newThisMonth:0 });

  // ─── ÉCOUTE EN TEMPS RÉEL AVEC onSnapshot ──────────────────────────────
  useEffect(() => {
    setLoading(true);

    const unsubscribeUsers = onSnapshot(
      collection(db, 'users'),
      async (snapshot) => {
        const userDocs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        try {
          const ordersSnap = await getDocs(collection(db, 'orders'));
          const allOrders = ordersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          
          const userList = userDocs.map(u => {
            const orders = allOrders.filter(o => o.userId === u.id);
            const totalSpent = orders.reduce((s, o) => s + (o.total || o.totalAmount || 0), 0);
            return { 
              ...u, 
              orders, 
              totalSpent, 
              orderCount: orders.length, 
              averageOrderValue: orders.length > 0 ? totalSpent / orders.length : 0 
            };
          });
          
          setUsers(userList);
          
          const first = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
          setStats({ 
            total: userList.length, 
            active: userList.filter(u => !u.isBanned).length, 
            banned: userList.filter(u => u.isBanned).length, 
            newThisMonth: userList.filter(u => u.createdAt && new Date(u.createdAt.seconds * 1000) >= first).length 
          });
          
          setLoading(false);
        } catch (error) {
          console.error('❌ Error loading orders:', error);
          setLoading(false);
          toast.error('Failed to load user orders');
        }
      },
      (error) => {
        console.error('❌ Real-time users error:', error);
        setLoading(false);
        toast.error('Failed to load users');
      }
    );

    return () => unsubscribeUsers();
  }, []);

  useEffect(() => {
    let f = [...users];
    if (searchTerm) { const s=searchTerm.toLowerCase(); f=f.filter(u=>u.name?.toLowerCase().includes(s)||u.email?.toLowerCase().includes(s)||u.phone?.includes(s)); }
    if (filterStatus==="active") f=f.filter(u=>!u.isBanned);
    if (filterStatus==="banned") f=f.filter(u=>u.isBanned);
    f.sort((a,b)=>{
      const vals={ name:[a.name?.toLowerCase()||"",b.name?.toLowerCase()||""], email:[a.email?.toLowerCase()||"",b.email?.toLowerCase()||""], orders:[a.orderCount||0,b.orderCount||0], spent:[a.totalSpent||0,b.totalSpent||0], created:[a.createdAt?.seconds||0,b.createdAt?.seconds||0], lastLogin:[a.lastLogin?.seconds||0,b.lastLogin?.seconds||0] };
      const [av,bv]=vals[sortBy]||vals.name;
      return sortOrder==="asc"?(av>bv?1:-1):(av<bv?1:-1);
    });
    setFilteredUsers(f);
  },[users,searchTerm,filterStatus,sortBy,sortOrder]);

  useEffect(() => {
  setLoading(true);

  // Écouter les utilisateurs en temps réel
  const unsubscribeUsers = onSnapshot(
    collection(db, 'users'),
    async (snapshot) => {
      const userDocs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Récupérer les commandes pour le calcul des totaux (une seule fois)
      try {
        const ordersSnap = await getDocs(collection(db, 'orders'));
        const allOrders = ordersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        
        const userList = userDocs.map(u => {
          const orders = allOrders.filter(o => o.userId === u.id);
          const totalSpent = orders.reduce((s, o) => s + (o.total || o.totalAmount || 0), 0);
          return { 
            ...u, 
            orders, 
            totalSpent, 
            orderCount: orders.length, 
            averageOrderValue: orders.length > 0 ? totalSpent / orders.length : 0 
          };
        });
        
        setUsers(userList);
        
        // Mettre à jour les stats
        const first = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
        setStats({ 
          total: userList.length, 
          active: userList.filter(u => !u.isBanned).length, 
          banned: userList.filter(u => u.isBanned).length, 
          newThisMonth: userList.filter(u => u.createdAt && new Date(u.createdAt.seconds * 1000) >= first).length 
        });
        
        setLoading(false);
      } catch (error) {
        console.error('❌ Error loading orders:', error);
        setLoading(false);
        toast.error('Failed to load user orders');
      }
    },
    (error) => {
      console.error('❌ Real-time users error:', error);
      setLoading(false);
      toast.error('Failed to load users');
    }
  );

  // Nettoyer l'écouteur au démontage
  return () => unsubscribeUsers();
}, []);

  const toggleBanUser = async (userId, isBanned) => {
    try {
      await updateDoc(doc(db,"users",userId),{isBanned:!isBanned});
      setUsers(prev=>prev.map(u=>u.id===userId?{...u,isBanned:!isBanned}:u));
      if (selectedUser?.id===userId) setSelectedUser(p=>({...p,isBanned:!isBanned}));
      toast.success(`User ${!isBanned?"banned":"unbanned"} successfully`);
    } catch(e) { toast.error("Failed to update user status"); }
  };

  const exportToCSV = () => {
    const csv=[["Name","Email","Phone","Orders","Total Spent","Status","Created At"].join(","),...filteredUsers.map(u=>[u.name||"",u.email||"",u.phone||"",u.orderCount||0,u.totalSpent||0,u.isBanned?"Banned":"Active",u.createdAt?new Date(u.createdAt.seconds*1000).toLocaleDateString():""].join(","))].join("\n");
    const a=Object.assign(document.createElement("a"),{href:URL.createObjectURL(new Blob([csv],{type:"text/csv"})),download:`users_${new Date().toISOString().split("T")[0]}.csv`});
    a.click(); URL.revokeObjectURL(a.href); toast.success("Exported!");
  };

  const formatDate = ts => {
    if (!ts) return "Never";
    const d=ts.seconds?new Date(ts.seconds*1000):new Date(ts);
    return d.toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"});
  };

  const openModal = (user, mode="view") => { setSelectedUser(user); setModalMode(mode); setIsModalOpen(true); };

  const COL = "56px 1fr 1fr 90px 130px 100px 80px 130px";

  if (loading && users.length===0) return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:400,gap:16}}>
      <div style={{ width: 44, height: 44, borderRadius: '50%', border: '3px solid rgba(236,72,153,.3)', borderTopColor: '#ec4899', animation: 'spin .8s linear infinite' }} />      <p style={{color:"#64748b",fontSize:13,fontWeight:600}}>Loading users…</p>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  return (
    <motion.div initial={{opacity:0}} animate={{opacity:1}} style={{paddingBottom:48}}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} input::placeholder{color:#334155}`}</style>

      {/* ── Header ── */}
      <div style={{marginBottom:20}}>
        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",flexWrap:"wrap",gap:12,marginBottom:18}}>
          <div>
            <p style={{fontSize:9,textTransform:"uppercase",letterSpacing:".18em",color:"pink",marginBottom:4}}>Management</p>
            <h1 style={{fontSize:22,fontWeight:800,color:"#fff",display:"flex",alignItems:"center",gap:10,marginBottom:3}}>
              <UsersIcon size={20} color="pink"/> Users
            </h1>
            <p style={{fontSize:11,color:"#475569"}}>Manage and monitor all registered Pi Network users</p>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <Btn variant="ghost" onClick={() => {}} disabled={loading}>
              <RefreshCw size={13} style={{animation:loading?"spin .8s linear infinite":"none"}}/>
              {loading?"Loading…":"Live ✅"}
            </Btn>
            <Btn variant="amber" onClick={exportToCSV}>
              <Download size={13}/> Export CSV
            </Btn>
          </div>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12}}>
          <MiniStat label="Total Users"    value={stats.total}        accent="#f59e0b" icon={UsersIcon}   index={0}/>
          <MiniStat label="Active"         value={stats.active}       accent="#34d399" icon={CheckCircle} index={1}/>
          <MiniStat label="Banned"         value={stats.banned}       accent="#f87171" icon={Slash}       index={2}/>
          <MiniStat label="New This Month" value={stats.newThisMonth} accent="#a78bfa" icon={Calendar}    index={3}/>
        </div>
      </div>

      {/* ── Toolbar ── */}
      <GlassPanel style={{padding:"12px 16px",marginBottom:14,display:"flex",flexWrap:"wrap",gap:10,alignItems:"center"}}>
        <div style={{flex:1,minWidth:180,position:"relative"}}>
          <Search size={12} style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:"#475569",pointerEvents:"none"}}/>
          <DarkInput value={searchTerm} onChange={e=>setSearchTerm(e.target.value)} placeholder="Search name, email, phone…" style={{paddingLeft:28}}/>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:6}}>
          <DarkSelect value={sortBy} onChange={e=>setSortBy(e.target.value)}>
            <option value="name">Name</option>
            <option value="email">Email</option>
            <option value="orders">Orders</option>
            <option value="spent">Spent</option>
            <option value="created">Created</option>
            <option value="lastLogin">Last Login</option>
          </DarkSelect>
          <button onClick={()=>setSortOrder(o=>o==="asc"?"desc":"asc")} style={{background:"rgba(255,255,255,.06)",border:"1px solid rgba(255,255,255,.08)",borderRadius:8,width:30,height:30,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",color:"#94a3b8",flexShrink:0}}>
            {sortOrder==="asc"?<ArrowUp size={12}/>:<ArrowDown size={12}/>}
          </button>
        </div>
        <DarkSelect value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="banned">Banned</option>
        </DarkSelect>
        <p style={{fontSize:10,color:"#334155",marginLeft:"auto",whiteSpace:"nowrap"}}>
          <span style={{color:"#f59e0b",fontWeight:700}}>{filteredUsers.length}</span> / {users.length} users
        </p>
      </GlassPanel>

      {/* ── Table ── */}
      <GlassPanel>
        {/* Header */}
        <div style={{display:"grid",gridTemplateColumns:COL,gap:8,padding:"10px 16px",borderBottom:"1px solid rgba(255,255,255,.05)",fontSize:9,textTransform:"uppercase",letterSpacing:".12em",color:"#334155",fontWeight:700,alignItems:"center"}}>
          <div/><div>User</div><div>Contact</div><div>Provider</div><div>Orders / Spent</div><div>Last Login</div><div>Status</div><div>Actions</div>
        </div>

        {filteredUsers.length===0&&!loading && (
          <div style={{padding:"48px 24px",textAlign:"center"}}>
            <UsersIcon size={32} style={{margin:"0 auto 12px",opacity:.3,display:"block",color:"#334155"}}/>
            <p style={{fontSize:13,fontWeight:600,color:"#334155"}}>No users found</p>
          </div>
        )}

        {filteredUsers.map((user,idx) => (
          <motion.div key={user.id} initial={{opacity:0}} animate={{opacity:1}} transition={{delay:Math.min(idx*.025,.4)}}
            style={{display:"grid",gridTemplateColumns:COL,gap:8,padding:"12px 16px",borderBottom:"1px solid rgba(255,255,255,.03)",alignItems:"center",transition:"background .15s",cursor:"pointer"}}
            onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,.02)"}
            onMouseLeave={e=>e.currentTarget.style.background="transparent"}
            onClick={()=>openModal(user,"view")}
          >
            {/* Avatar */}
            <div onClick={e=>{e.stopPropagation();if(user.profilePic)openModal(user,"profilePic");}}>
              <Avatar user={user} size={38}/>
            </div>

            {/* Name */}
            <div>
              <p style={{fontSize:12,fontWeight:700,color:"#e2e8f0"}}>{user.name||"Unknown"}</p>
              <p style={{fontSize:10,color:"#334155",marginTop:2,fontFamily:"monospace"}}>#{user.id.substring(0,8).toUpperCase()}</p>
            </div>

            {/* Contact */}
            <div>
              <div style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:"#94a3b8",marginBottom:3}}>
                <Mail size={10} color="#475569"/><span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{user.email||"—"}</span>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:5,fontSize:10,color:"#475569"}}>
                <Phone size={10}/>{user.phone||"—"}
              </div>
            </div>

            {/* Provider */}
            <div onClick={e=>e.stopPropagation()}><ProviderBadge provider={user.provider}/></div>

            {/* Orders */}
            <div>
              <div style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:"#e2e8f0",fontWeight:600,marginBottom:2}}>
                <Package size={10} color="#f59e0b"/>{user.orderCount||0} orders
              </div>
              <div style={{display:"flex",alignItems:"center",gap:5,fontSize:10,color:"#475569"}}>
                <ShoppingBag size={10}/>{user.totalSpent?`${user.totalSpent.toFixed(2)} π`:"0 π"}
              </div>
            </div>

            {/* Last Login */}
            <div style={{display:"flex",alignItems:"center",gap:5,fontSize:10,color:"#475569"}}>
              <LogIn size={10}/>{formatDate(user.lastLogin)}
            </div>

            {/* Status */}
            <div onClick={e=>e.stopPropagation()}><StatusBadge banned={user.isBanned}/></div>

            {/* Actions */}
            <div style={{display:"flex",gap:5}} onClick={e=>e.stopPropagation()}>
              <Btn size="xs" variant="ghost" onClick={()=>openModal(user,"view")}><Eye size={10}/></Btn>
              <Btn size="xs" variant={user.isBanned?"green":"red"} onClick={()=>toggleBanUser(user.id,user.isBanned)}>
                {user.isBanned?<><CheckCircle size={10}/>Unban</>:<><UserX size={10}/>Ban</>}
              </Btn>
            </div>
          </motion.div>
        ))}

        {filteredUsers.length>0&&(
          <div style={{padding:"10px 16px",borderTop:"1px solid rgba(255,255,255,.04)"}}>
            <p style={{fontSize:10,color:"#334155"}}>
              Showing <span style={{color:"#f59e0b",fontWeight:700}}>{filteredUsers.length}</span> of <span style={{color:"#f59e0b",fontWeight:700}}>{users.length}</span> users
            </p>
          </div>
        )}
      </GlassPanel>

      {/* ── Modal ── */}
      <AnimatePresence>
        {isModalOpen&&selectedUser&&(
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            style={{position:"fixed",inset:0,background:"rgba(0,0,0,.65)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:16,zIndex:50}}
            onClick={()=>setIsModalOpen(false)}
          >
            <motion.div initial={{scale:.95,opacity:0,y:14}} animate={{scale:1,opacity:1,y:0}} exit={{scale:.95,opacity:0,y:14}} transition={{duration:.22,ease:[.22,1,.36,1]}}
              style={{background:"#0d1526",border:"1px solid rgba(255,255,255,.1)",borderRadius:18,width:"100%",maxWidth:modalMode==="orders"?620:520,maxHeight:"88vh",overflow:"hidden",display:"flex",flexDirection:"column"}}
              onClick={e=>e.stopPropagation()}
            >
              {/* Header */}
              <div style={{padding:"16px 20px",borderBottom:"1px solid rgba(255,255,255,.06)",display:"flex",alignItems:"center",gap:14}}>
                <Avatar user={selectedUser} size={44}/>
                <div style={{flex:1,minWidth:0}}>
                  <p style={{fontSize:14,fontWeight:700,color:"#fff",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{selectedUser.name||"Unknown"}</p>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginTop:4,flexWrap:"wrap"}}>
                    <StatusBadge banned={selectedUser.isBanned}/>
                    <ProviderBadge provider={selectedUser.provider}/>
                  </div>
                </div>
                {/* Tabs */}
                <div style={{display:"flex",gap:4,flexShrink:0}}>
                  {["view","orders",...(selectedUser.profilePic?["profilePic"]:[])].map(mode=>(
                    <button key={mode} onClick={()=>setModalMode(mode)} style={{padding:"4px 10px",borderRadius:8,fontSize:10,fontWeight:600,cursor:"pointer",background:modalMode===mode?"rgba(245,158,11,.15)":"rgba(255,255,255,.05)",color:modalMode===mode?"#f59e0b":"#475569",border:modalMode===mode?"1px solid rgba(245,158,11,.3)":"1px solid rgba(255,255,255,.08)",transition:"all .15s"}}>
                      {mode==="view"?"Profile":mode==="orders"?`Orders (${selectedUser.orderCount||0})`:"Photo"}
                    </button>
                  ))}
                </div>
                <button onClick={()=>setIsModalOpen(false)} style={{background:"rgba(255,255,255,.06)",border:"1px solid rgba(255,255,255,.08)",borderRadius:8,padding:"5px 8px",cursor:"pointer",color:"#94a3b8",display:"flex",flexShrink:0}}>
                  <X size={14}/>
                </button>
              </div>

              {/* Body */}
              <div style={{padding:20,overflowY:"auto",flex:1}}>

                {/* ── Profile tab ── */}
                {modalMode==="view"&&(
                  <div style={{display:"flex",flexDirection:"column",gap:14}}>
                    {/* KPIs */}
                    <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
                      {[{label:"Total Orders",value:selectedUser.orderCount||0,accent:"#f59e0b"},{label:"Total Spent",value:`${(selectedUser.totalSpent||0).toFixed(2)} π`,accent:"#a78bfa"},{label:"Avg. Order",value:`${(selectedUser.averageOrderValue||0).toFixed(2)} π`,accent:"#34d399"}].map(({label,value,accent})=>(
                        <div key={label} style={{background:`${accent}0d`,border:`1px solid ${accent}25`,borderRadius:10,padding:"10px 12px",textAlign:"center"}}>
                          <p style={{fontSize:9,textTransform:"uppercase",letterSpacing:".12em",color:accent,opacity:.7,marginBottom:5}}>{label}</p>
                          <p style={{fontSize:16,fontWeight:700,color:"#fff"}}>{value}</p>
                        </div>
                      ))}
                    </div>
                    {/* Info grid */}
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                      <MetaCell span label="Email"     value={selectedUser.email}/>
                      <MetaCell label="Phone"          value={selectedUser.phone}/>
                      <MetaCell label="Provider"       value={selectedUser.provider||"Email"}/>
                      <MetaCell label="Last Login"     value={formatDate(selectedUser.lastLogin)}/>
                      <MetaCell label="Registered"     value={formatDate(selectedUser.createdAt)}/>
                      <MetaCell label="User ID"        value={<span style={{fontFamily:"monospace",fontSize:11,color:"#f59e0b"}}>{selectedUser.id?.substring(0,16)}</span>}/>
                      {selectedUser.address&&<MetaCell span label="Address" value={`${selectedUser.address.line1||""}, ${selectedUser.address.city||""}, ${selectedUser.address.state||""} ${selectedUser.address.pin||""}`}/>}
                    </div>
                    {/* Actions */}
                    <div style={{display:"flex",gap:8,paddingTop:8,borderTop:"1px solid rgba(255,255,255,.05)",flexWrap:"wrap"}}>
                      <Btn variant="ghost" style={{flex:1,justifyContent:"center"}} onClick={()=>setModalMode("orders")}>
                        <ShoppingBag size={12}/> View Orders ({selectedUser.orderCount||0})
                      </Btn>
                      <Btn variant={selectedUser.isBanned?"green":"red"} style={{flex:1,justifyContent:"center"}} onClick={()=>toggleBanUser(selectedUser.id,selectedUser.isBanned)}>
                        {selectedUser.isBanned?<><CheckCircle size={12}/>Unban User</>:<><UserX size={12}/>Ban User</>}
                      </Btn>
                    </div>
                  </div>
                )}

                {/* ── Orders tab ── */}
                {modalMode==="orders"&&(
                  !selectedUser.orders?.length
                    ? <div style={{padding:"32px 0",textAlign:"center"}}><ShoppingBag size={28} style={{margin:"0 auto 10px",opacity:.3,display:"block",color:"#334155"}}/><p style={{color:"#334155",fontSize:12}}>No orders yet</p></div>
                    : <div style={{display:"flex",flexDirection:"column",gap:8}}>
                        {selectedUser.orders.map(order=>(
                          <div key={order.id} style={{background:"rgba(255,255,255,.03)",border:"1px solid rgba(255,255,255,.06)",borderRadius:12,padding:"12px 14px"}}>
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                              <div>
                                <p style={{fontSize:12,fontWeight:700,color:"#e2e8f0",fontFamily:"monospace"}}>#{(order.orderId||order.id||"").substring(0,8).toUpperCase()}</p>
                                <p style={{fontSize:10,color:"#475569",marginTop:2}}>{formatDate(order.orderDate)}</p>
                              </div>
                              <OrderBadge status={order.status}/>
                            </div>
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                              <p style={{fontSize:10,color:"#475569"}}>{order.items?.length||0} item{order.items?.length!==1?"s":""}</p>
                              <p style={{fontSize:13,fontWeight:700,color:"#f59e0b"}}>{(order.total||0).toFixed(2)} π</p>
                            </div>
                          </div>
                        ))}
                      </div>
                )}

                {/* ── Photo tab ── */}
                {modalMode==="profilePic"&&selectedUser.profilePic&&(
                  <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:16}}>
                    <img src={selectedUser.profilePic} alt={selectedUser.name} style={{maxWidth:"100%",maxHeight:340,borderRadius:14,border:"1px solid rgba(255,255,255,.08)",objectFit:"cover"}}/>
                    <a href={selectedUser.profilePic} target="_blank" rel="noopener noreferrer" style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:11,color:"#f59e0b",textDecoration:"none",fontWeight:600}}>
                      <ExternalLink size={12}/> Open in new tab
                    </a>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Users;
